import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Clickable, Text } from '@moda/om';
import { formatPrice, useForm } from '@moda/portal-stanchions';
import { useQuery } from '@apollo/client/react';
import { SizeInventoryFragment } from '../../../generated/types';
import { useCountry } from '../../CountrySelector/useCountry';
import { useAddressesQuery } from '../../../hooks/useAddress';
import { useCookies } from '../../../hooks/useCookies';
import { useSmartyStreetsState } from '../../../hooks/useSmartyStreetsState';
import { usePageType } from '../../../hooks/usePageType';
import { tc } from '../../../lib/trackingContext';
import { GET_DELIVERY_ESTIMATE_QUERY } from './deliveryEstimate.rest';
import {
  COUNTDOWN_WINDOW_MS,
  DeliveryOption,
  RANGE_COPY,
  SAME_DAY_AREA_COPY,
  SAME_DAY_HOURS_COPY,
  buildDeliveryView,
  formatCountdown,
  formatCutoff
} from './deliveryOptions';
import { useCountryCodeAndCurrency } from './useCountryCodeAndCurrency';
import { useTimeLeft } from './useTimeLeft';

import './ProductDeliveryOptions.scss';

const validations = {
  zipCode: {
    pattern: {
      message: 'Please enter a 5-digit US zip code.',
      value: /^\b\d{5}(-\d{4})?\b$/
    },
    required: {
      message: 'Please enter a zip code.',
      value: true
    }
  }
};

interface Props {
  variantId: string;
  fulfilmentType: number | null;
  size: SizeInventoryFragment | null;
}

const US_ZIP_CODE = /^\d{5}$/;

const formatShipsBy = (date: string) =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York'
  });

export const ProductDeliveryOptions: React.FC<Props> = ({ variantId, fulfilmentType, size }) => {
  const { country } = useCountry();
  const { addresses } = useAddressesQuery();
  const {
    cookies: { cloudfrontViewerCountry, cloudfrontViewerPostalCode }
  } = useCookies();
  const pagetype = usePageType();
  const [enteredZipCode, setEnteredZipCode] = useState<string>();
  const [editing, setEditing] = useState(false);
  const zipInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) zipInputRef.current?.focus();
  }, [editing]);

  const isUS = country.alpha3Code === 'USA';
  const savedZipCode =
    addresses[0]?.attributes.countryCode === 'US' ? addresses[0].attributes.postalCode : undefined;
  const guessedZipCode =
    cloudfrontViewerCountry === 'US' &&
    cloudfrontViewerPostalCode &&
    US_ZIP_CODE.test(cloudfrontViewerPostalCode)
      ? cloudfrontViewerPostalCode
      : undefined;
  const zipCode = isUS ? enteredZipCode || savedZipCode || guessedZipCode : undefined;
  const zipCodeIsGuess = isUS && !enteredZipCode && !savedZipCode && Boolean(guessedZipCode);

  const {
    loading: loadingState,
    isInvalid: invalidZipCode,
    abbreviation
  } = useSmartyStreetsState(zipCode);
  const { updatedCountryCode, updatedCurrency } = useCountryCodeAndCurrency(country.name);

  const { data, loading } = useQuery(GET_DELIVERY_ESTIMATE_QUERY, {
    variables: {
      variantId,
      zipCode,
      countryCode: updatedCountryCode,
      currency: updatedCurrency,
      stateCode: zipCode ? abbreviation : undefined
    },
    skip: loadingState
  });

  const estimate = data?.deliveryEstimate;
  const view = buildDeliveryView({
    estimate,
    fulfilmentType,
    size,
    hasZipCode: Boolean(zipCode),
    now: new Date()
  });
  const timeLeft = useTimeLeft(view.kind === 'options' ? view.cutoffAt : null);

  const onSubmit = useCallback(
    ({ zipCode }: { zipCode: string }) => {
      tc.track('Delivery Estimated with Zip Code', { addToTrackingEvent: { pagetype } });
      setEnteredZipCode(zipCode);
      setEditing(false);
    },
    [pagetype]
  );

  const { handleSubmit, errors: fieldErrors } = useForm({ onSubmit, validations });

  if (view.kind === 'hidden') return null;

  const invitesZipCheck = view.kind === 'options' && view.sameDay === 'check-zip';

  const price = (option: DeliveryOption) =>
    option.charges === 0 ? 'Free' : formatPrice(estimate?.currency ?? 'USD', option.charges);

  const sameDayHeadline = () => {
    if (view.kind !== 'options' || !view.cutoffAt) return 'Arrives today';
    if (timeLeft !== null && timeLeft <= COUNTDOWN_WINDOW_MS) {
      return `Arrives today if you order within ${formatCountdown(timeLeft)}`;
    }
    return `Arrives today if you order by ${formatCutoff(view.cutoffAt)} ET`;
  };

  return (
    <div
      className={classNames('ProductDeliveryOptions', {
        'ProductDeliveryOptions--loading': loading
      })}
    >
      <div className="ProductDeliveryOptions__header">
        <Text treatment="eyebrow">
          {zipCode && !editing ? `Delivery to ${zipCode}` : 'Delivery'}
        </Text>

        {editing && (
          <form className="ProductDeliveryOptions__form" onSubmit={handleSubmit}>
            <input
              className="ProductDeliveryOptions__zip"
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={10}
              placeholder="00000"
              name="zipCode"
              aria-label="Zip code"
              defaultValue={zipCode}
              ref={zipInputRef}
              onKeyDown={event => event.key === 'Escape' && setEditing(false)}
            />
            <Clickable type="submit" className="ProductDeliveryOptions__link">
              Check
            </Clickable>
          </form>
        )}

        {!editing && isUS && !invitesZipCheck && (
          <Clickable className="ProductDeliveryOptions__link" onClick={() => setEditing(true)}>
            {zipCodeIsGuess ? 'Not your zip?' : zipCode ? 'Change' : 'Enter zip code'}
          </Clickable>
        )}
      </div>

      {fieldErrors.zipCode && (
        <Text treatment="body2" color="code-red">
          {fieldErrors.zipCode.message}
        </Text>
      )}

      {invalidZipCode && (
        <Text treatment="body2" color="code-red">
          We couldn&apos;t find that zip code.
        </Text>
      )}

      {view.kind === 'range' && <p className="ProductDeliveryOptions__headline">{RANGE_COPY}</p>}

      {view.kind === 'size-ships-later' && (
        <p className="ProductDeliveryOptions__headline">
          {view.shipsBy
            ? `This size ships by ${formatShipsBy(view.shipsBy)}`
            : 'This size ships later than other sizes'}
        </p>
      )}

      {view.kind === 'options' && (
        <>
          <p className="ProductDeliveryOptions__headline">
            <span>
              {view.fastest.sameDay
                ? sameDayHeadline()
                : `Arrives by ${view.fastest.date} with ${view.fastest.label}`}
            </span>
            <span className="ProductDeliveryOptions__price"> · {price(view.fastest)}</span>
          </p>

          {invitesZipCheck && !editing && (
            <p className="ProductDeliveryOptions__note">
              <span>{SAME_DAY_AREA_COPY}</span>
              <Clickable className="ProductDeliveryOptions__link" onClick={() => setEditing(true)}>
                Check your zip
              </Clickable>
            </p>
          )}

          {view.sameDay === 'after-cutoff' && (
            <p className="ProductDeliveryOptions__note">{SAME_DAY_HOURS_COPY}</p>
          )}

          {view.others.length > 0 && (
            <ul className="ProductDeliveryOptions__others">
              {view.others.map(option => (
                <li key={option.id}>
                  {option.date} · {option.label} · {price(option)}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};
